import React, { useState, useEffect } from 'react';
import { useNotes } from '../../context/NotesContext';
import useScreenSize from '../../hooks/useScreenSize';
import RichTextEditor from './RichTextEditor';
import AISuggestions from './AISuggestions';
import '../../styles/glass-morphism.css';

function NoteEditor() {
  const { selectedNote, categories, updateNote, deleteNote, selectNote } = useNotes();
  const { isMobile } = useScreenSize();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveTimeout, setSaveTimeout] = useState(null);

  // Load selected note
  useEffect(() => {
    if (selectedNote) {
      setTitle(selectedNote.title);
      setContent(selectedNote.content);
      setCategoryId(selectedNote.category_id || '');
      setIsFavorite(selectedNote.is_favorite || false);
    } else {
      // Clear form when no note is selected
      setTitle('');
      setContent('');
      setCategoryId('');
      setIsFavorite(false);
    }
  }, [selectedNote]);

  // Auto-save with debounce
  useEffect(() => {
    // Don't auto-save if no note is selected or if the content matches the current note
    if (!selectedNote) return;
    if (title === selectedNote.title && content === selectedNote.content) return;

    // Clear any existing timeout
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }

    // Set new timeout for auto-save
    const timeout = setTimeout(async () => {
      if (selectedNote) {
        console.log('Auto-saving note:', selectedNote.id, 'with title:', title);
        setIsSaving(true);
        await updateNote(selectedNote.id, {
          title,
          content,
          category_id: categoryId || null,
          is_favorite: isFavorite,
        });
        setIsSaving(false);
      }
    }, 1000); // Auto-save after 1 second of no typing

    setSaveTimeout(timeout);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, content, selectedNote?.id]);

  const handleCategoryChange = async (newCategoryId) => {
    setCategoryId(newCategoryId);
    if (selectedNote) {
      await updateNote(selectedNote.id, {
        category_id: newCategoryId || null,
      });
    }
  };

  const handleFavoriteToggle = async () => {
    const newValue = !isFavorite;
    setIsFavorite(newValue);
    if (selectedNote) {
      await updateNote(selectedNote.id, {
        is_favorite: newValue,
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedNote) return;
    if (window.confirm('Are you sure you want to delete this note?')) {
      await deleteNote(selectedNote.id);
      selectNote(null);
    }
  };

  const handleContentChange = (newContent) => {
    setContent(newContent);
  };

  const handleInsertSuggestion = (suggestion) => {
    // Insert the suggestion as a new paragraph at the end of the content
    const newContent = content + `<p>${suggestion}</p>`;
    setContent(newContent);
  };

  if (!selectedNote) {
    return (
      <div style={styles.emptyContainer}>
        <div style={styles.emptyContent}>
          <h2 style={styles.emptyTitle}>No note selected</h2>
          <p style={styles.emptyText}>Select a note from the list or create a new one to start editing</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Toolbar */}
      <div
        style={{
          ...styles.toolbar,
          padding: isMobile ? '10px 12px' : '12px 16px',
          flexWrap: isMobile ? 'wrap' : 'nowrap',
        }}
      >
        <div style={styles.toolbarLeft}>
          <select
            value={categoryId}
            onChange={(e) => handleCategoryChange(e.target.value)}
            style={styles.categorySelect}
          >
            <option value="">No Category</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.toolbarRight}>
          <button
            type="button"
            onClick={handleFavoriteToggle}
            className="icon-action"
            style={styles.favoriteButton}
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            aria-label={isFavorite ? 'Remove note from favorites' : 'Add note to favorites'}
            aria-pressed={isFavorite}
          >
            {isFavorite ? '★' : '☆'}
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="destructive-action"
            style={{
              ...styles.deleteButton,
              minWidth: isMobile ? '44px' : 'auto',
              minHeight: isMobile ? '44px' : 'auto',
            }}
            title="Delete note"
          >
            Delete
          </button>
          {isSaving && <span style={styles.savingIndicator}>Saving...</span>}
        </div>
      </div>

      {/* Editor */}
      <div
        style={{
          ...styles.editorContainer,
          padding: isMobile ? '16px' : '24px',
        }}
      >
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={{
            ...styles.titleInput,
            fontSize: isMobile ? '1.5rem' : '2rem',
          }}
          placeholder="Note Title"
        />
        <RichTextEditor
          content={content}
          onChange={handleContentChange}
          placeholder="Start writing your note..."
        />
        <AISuggestions
          noteContent={content}
          noteTitle={title}
          onInsertSuggestion={handleInsertSuggestion}
        />
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        <span style={styles.footerText}>
          Last updated: {new Date(selectedNote.updated_at).toLocaleString()}
        </span>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    background: 'rgba(255, 255, 255, 0.95)',
  },
  emptyContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    background: 'rgba(255, 255, 255, 0.95)',
  },
  emptyContent: {
    textAlign: 'center',
    padding: '32px',
  },
  emptyTitle: {
    color: '#333',
    fontSize: '1.5rem',
    marginBottom: '12px',
  },
  emptyText: {
    color: '#666',
    fontSize: '1rem',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid #e0e0e0',
    background: 'white',
  },
  toolbarLeft: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  toolbarRight: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  categorySelect: {
    padding: '6px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    background: 'white',
    fontSize: '14px',
    cursor: 'pointer',
  },
  favoriteButton: {
    background: '#fff8e1',
    border: '1px solid #e2b203',
    borderRadius: '8px',
    width: '40px',
    height: '40px',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#7a4f01',
  },
  deleteButton: {
    background: '#b42318',
    color: 'white',
    border: 'none',
    padding: '6px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  savingIndicator: {
    color: '#666',
    fontSize: '14px',
    fontStyle: 'italic',
  },
  editorContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: '24px',
    overflowY: 'auto',
  },
  titleInput: {
    border: 'none',
    outline: 'none',
    fontSize: '2rem',
    fontWeight: '600',
    color: '#333',
    marginBottom: '16px',
    padding: '8px 0',
    background: 'transparent',
  },
  footer: {
    padding: '12px 16px',
    borderTop: '1px solid #e0e0e0',
    background: 'white',
  },
  footerText: {
    color: '#999',
    fontSize: '0.85rem',
  },
};

export default NoteEditor;
